import { AiOutlineMenu, AiOutlineClose } from "react-icons/ai";
import { Icon as Iconify } from "@iconify/react";
import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";

export default function Nav() {
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [raised, setRaised] = useState(false);

  useEffect(() => {
    const onScroll = () => setRaised(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => (document.body.style.overflow = "");
  }, [open]);

  const isActive = (path) => location.pathname === path;
  const go = (to) => {
    navigate(to);
    setOpen(false);
  };

  const baseItem =
    "relative px-3 py-2 rounded-xl min-w-[4.5rem] grid place-items-center " +
    "text-base font-semibold text-white/90 hover:text-white transition-colors " +
    "cursor-pointer select-none focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60 " +
    "active:scale-[0.98]";
  const activeItem =
    "bg-white text-[#697B68] font-semibold shadow-[0_3px_8px_-2px_rgba(0,0,0,0.25)] hover:text-[#697B68]";

  const Underline = ({ active }) => (
    <span
      className={[
        "pointer-events-none absolute -bottom-[6px] left-1/2 h-[2px] w-0 -translate-x-1/2",
        active ? "w-8" : "group-hover:w-8",
        "bg-gradient-to-r from-[#E09767] to-[#EFB78D]",
        "transition-all duration-300 ease-out rounded-full",
      ].join(" ")}
    />
  );

  const menuItems = [
    { to: "/", label: "Inicio", icon: "mdi:home-outline" },
    { to: "/sobre-mi", label: "Sobre mi", icon: "mdi:account-outline" },
    { to: "/tyr", label: "TYR", icon: "mdi:briefcase-outline" },
    { to: "/links", label: "Links", icon: "mdi:link-variant" },
  ];

  return (
    <>
      <nav
        className={[
          "fixed z-[100] left-1/2 -translate-x-1/2 top-4",
          "nav-desktop items-center justify-between",
          "w-[min(68.75rem,92vw)] min-h-16 rounded-[1.375rem] px-3",
          "bg-[#697B68] text-white backdrop-blur-lg ring-1",
          "transition-all duration-300 ease-out will-change-transform",
          raised
            ? "translate-y-2 scale-[1.002] ring-white/15 shadow-[0_14px_30px_-12px_rgba(0,0,0,0.55),0_10px_18px_-10px_rgba(0,0,0,0.35)]"
            : "translate-y-0 ring-white/10 shadow-[inset_0_1px_0_rgba(255,255,255,0.12),inset_0_-6px_12px_rgba(0,0,0,0.35)]",
        ].join(" ")}
        role="navigation"
        aria-label="Principal"
      >
        <button
          onClick={() => navigate("/")}
          className="relative flex items-center gap-3 group cursor-pointer select-none
                     min-h-14 px-2 rounded-xl
                     focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60
                     active:scale-[0.98]"
        >
          <span className="absolute inset-0 rounded-xl bg-white/0 group-hover:bg-white/[0.05] transition-colors" />
          <img
            src="/logo.png"
            alt="Sebas Sotelo"
            className="h-10 w-10 rounded-full ring-1 ring-white/20"
          />
          <p className="font-semibold tracking-wide text-white">Sebas Sotelo</p>
        </button>

        <ul className="flex items-center gap-1.5 pr-2 h-full">
          {menuItems.map((item) => {
            const active = isActive(item.to);
            return (
              <li
                key={item.to}
                className={`group ${baseItem} ${active ? activeItem : ""}`}
                onClick={() => navigate(item.to)}
                aria-current={active ? "page" : undefined}
              >
                <span
                  className={`relative transition-colors duration-300 ${
                    active ? "text-[#697B68]" : ""
                  }`}
                >
                  {item.label}
                  <Underline active={active} />
                </span>
              </li>
            );
          })}
        </ul>
      </nav>

      <div
        className={[
          "nav-mobile fixed inset-x-0 top-0 z-[100]",
          "items-center justify-between px-4",
          "bg-[#697B68] text-white",
          "transition-all duration-200",
          raised ? "h-14 shadow-[0_6px_16px_-10px_rgba(0,0,0,0.6)]" : "h-16",
        ].join(" ")}
      >
        <button
          onClick={() => navigate("/")}
          className="flex items-center gap-2.5 cursor-pointer select-none
                     focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60 
                     active:scale-95 transition-transform"
        >
          <img
            src="/logo.png"
            alt="Sebas Sotelo"
            className="h-9 w-9 rounded-full ring-2 ring-white/10"
          />
          <span className="text-white font-bold tracking-wide text-[0.9375rem]">
            Sebas Sotelo
          </span>
        </button>

        <button
          onClick={() => setOpen((v) => !v)}
          aria-label={open ? "Cerrar menu" : "Abrir menu"}
          className={[
            "grid place-items-center text-white text-2xl",
            "rounded-xl h-10 w-10 transition-all duration-200",
            open
              ? "bg-white/15 rotate-90 scale-110"
              : "bg-white/10 active:scale-95",
          ].join(" ")}
        >
          {open ? <AiOutlineClose /> : <AiOutlineMenu />}
        </button>
      </div>

      <div
        className={[
          "nav-mobile-backdrop fixed inset-0 z-[80] bg-black/40",
          "transition-opacity duration-200",
          open ? "opacity-100" : "opacity-0 pointer-events-none",
        ].join(" ")}
        onClick={() => setOpen(false)}
      />

      <div
        className={[
          "nav-mobile-panel fixed left-0 right-0 z-[90]",
          "mx-3 rounded-2xl overflow-hidden",
          "transition-all duration-200 ease-out",
          open
            ? "top-[4.5rem] opacity-100 translate-y-0"
            : "top-[2.75rem] opacity-0 -translate-y-2 pointer-events-none",
        ].join(" ")}
      >
        <div className="bg-[#697B68]">
          <ul className="flex flex-col py-2">
            {menuItems.map((item) => {
              const active = isActive(item.to);
              return (
                <li key={item.to} className="w-full">
                  <button
                    onClick={() => go(item.to)}
                    className={[
                      "w-full text-left px-4 py-4",
                      "cursor-pointer select-none flex items-center gap-3",
                      "transition-colors duration-150",
                      active
                        ? "bg-[#566756] text-white"
                        : "text-white/90 hover:bg-white/10 hover:text-white",
                    ].join(" ")}
                    aria-current={active ? "page" : undefined}
                  >
                    <Iconify
                      icon={item.icon}
                      width={22}
                      height={22}
                      className="flex-shrink-0"
                    />

                    <span className="flex-1 text-base font-medium">
                      {item.label}
                    </span>
                  </button>

                  {active && (
                    <div className="h-[2px] w-full bg-gradient-to-r from-[#E09767] to-[#EFB78D]" />
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </>
  );
}
